import React, { useState, useEffect } from 'react'
import { Outlet, useLocation, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { 
  Box,
  Button,
  Container,
  Drawer,
  Typography,
  Stack,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Chip,
  CircularProgress,
  Tooltip
} from '@mui/material'
import {
  Person as PersonIcon,
  Logout as LogoutIcon,
  ExpandMore as ExpandMoreIcon,
  Lock as LockIcon
} from '@mui/icons-material'
import {
  LayoutSidebarLeftCollapseIcon,
  LayoutSidebarLeftExpandIcon
} from '../components/icons/TablerIcons'

import Navigation from '../components/Navigation'
import poolwerxLogo from '../assets/poolwerx-logo.png'
import pwLogoCompact from '../assets/pw-white.png'
import { useAuth } from '../contexts/AuthContext'
import { canAccessAdminPanel } from '../services/auth'
import StandaloneCardLayout from '../components/layouts/StandaloneCardLayout'
import AuthPageFooter from '../components/auth/AuthPageFooter'
import { getProfileImageUrl, getUserInitials } from '../utils/profileImage'
import { getRoleDisplayName, getRoleHexColor } from '../utils/roleDisplay'
import { poolwerxColors } from '../theme/colors'
import VersionDisplay from '../components/common/VersionDisplay'
import { useSidebarCollapsed } from '../hooks/useSidebarCollapsed'
import { useVersion } from '../hooks/useVersion'
import DevEnvironmentBanner from '../components/DevEnvironmentBanner'
import ReleaseNotesAutoLauncher from '../components/release/ReleaseNotesAutoLauncher'
import NotificationBell from '../components/notifications/NotificationBell'
import { FeedbackUIProvider } from '../components/feedback/FeedbackUIContext'
import HelpFeedbackDrawer from '../components/feedback/HelpFeedbackDrawer'
import HelpFeedbackFab from '../components/feedback/HelpFeedbackFab'

const RAIL_WIDTH = 64
const FULL_WIDTH = 280
const WIDE_CONTENT_MAX = 2400
const WIDE_ROUTES: Array<{ path: string; match: 'prefix' | 'exact' }> = [
  { path: '/admin/platform-reports', match: 'prefix' },
  { path: '/admin/data-exceptions/activity', match: 'exact' },
  { path: '/admin/franchise-partners', match: 'exact' },
  { path: '/admin/data-sync/history', match: 'exact' },
  { path: '/admin/data-sync/active', match: 'exact' },
  { path: '/admin/system/audit-logs', match: 'exact' },
  { path: '/admin/system-connections', match: 'exact' },
]

const AdminLayout: React.FC = () => {
  const { user, logout, loading, isAuthenticated } = useAuth()
  const location = useLocation()
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null)
  const [imageError, setImageError] = useState(false)
  const [collapsed, , toggleCollapsed] = useSidebarCollapsed()
  const version = useVersion()
  const drawerWidth = collapsed ? RAIL_WIDTH : FULL_WIDTH
  const isWide = WIDE_ROUTES.some(({ path, match }) =>
    match === 'exact' ? location.pathname === path : location.pathname.startsWith(path)
  )

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget)
  }

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null)
  }

  const handleLogout = async () => {
    handleUserMenuClose()
    await logout()
  }

  // SECURITY: Redirect to login if not authenticated
  // Don't pass admin paths as redirect — RoleRedirect at "/" handles post-login routing
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      console.log('🔒 AdminLayout: Not authenticated, redirecting to login')
      window.location.href = '/login'
    }
  }, [isAuthenticated, loading])

  // Publish the admin content-left offset (the live drawer width) on :root so
  // the global toast provider can anchor bottom-left of the content region,
  // clear of the sidebar. Only while the admin chrome (drawer) actually renders;
  // cleared otherwise so other layouts fall back to the 0 default.
  const showsChrome = !loading && isAuthenticated && !!user && canAccessAdminPanel(user)
  useEffect(() => {
    if (!showsChrome) return
    const root = document.documentElement
    root.style.setProperty('--app-content-left', `${drawerWidth}px`)
    return () => {
      root.style.removeProperty('--app-content-left')
    }
  }, [showsChrome, drawerWidth])

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <CircularProgress size={48} sx={{ color: poolwerxColors.teal, mb: 2 }} />
        <Typography variant="h6" color="textSecondary">
          ⚡ Checking authentication...
        </Typography>
      </Box>
    )
  }

  // Don't render admin content if not authenticated
  if (!isAuthenticated) {
    return null
  }

  // SECURITY: Block non-admin roles from admin panel
  if (user && !canAccessAdminPanel(user)) {
    return (
      <>
        <Helmet>
          <title>Access Denied | Inspector Tool</title>
        </Helmet>
        <StandaloneCardLayout headerTitle="Admin Access Required">
          <Stack spacing={3}>
            <Box sx={{ textAlign: 'center' }}>
              <LockIcon sx={{ fontSize: '4rem', color: poolwerxColors.teal, mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2, color: '#333' }}>
                This area is restricted
              </Typography>
              <Typography variant="body1" color="textSecondary">
                The Admin Panel is only available to Evolution team members.
                Use the link below to access your franchise.
              </Typography>
            </Box>
            <Stack spacing={2}>
              <Button
                component={Link}
                to="/choose-franchise"
                variant="contained"
                size="large"
                fullWidth
                sx={{
                  backgroundColor: poolwerxColors.teal,
                  '&:hover': { backgroundColor: '#006680' },
                  padding: '12px 30px',
                  fontSize: '16px',
                  borderRadius: 1,
                  textTransform: 'none',
                }}
              >
                Go to My Franchise
              </Button>
            </Stack>
            <AuthPageFooter user={user} />
          </Stack>
        </StandaloneCardLayout>
      </>
    )
  }

  return (
    <FeedbackUIProvider>
      <Helmet>
        <title>Inspector Tool - Admin Panel</title>
        <meta name="description" content="Inspector Tool - Admin panel for Poolwerx monitoring" />
        <link rel="icon" type="image/svg+xml" href="/favicon-admin.svg" />
        <link rel="alternate icon" href="/favicon-admin.svg" />
        <link rel="apple-touch-icon" href="/favicon-admin.svg" />
      </Helmet>

      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh'
      }}>
        <Box sx={{ display: 'flex', flex: 1 }}>
        {/* Admin Sidebar */}
        <Drawer
          variant="permanent"
          className="no-print"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            transition: (theme) => theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shortest
            }),
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              backgroundColor: '#007f98',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto', // Allow vertical scrolling of entire sidebar
              overflowX: 'visible', // Keep horizontal overflow visible for sub-menus
              transition: (theme) => theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shortest
              }),
            },
          }}
        >
          {/* Logo and Title Section */}
          <Box sx={{
            position: 'relative',
            p: collapsed ? 1 : 2,
            pt: collapsed ? 7 : 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderBottom: '1px solid rgba(255,255,255,0.2)',
            mb: 2,
            backgroundColor: '#222222'
          }}>
            <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
              <IconButton
                size="small"
                onClick={toggleCollapsed}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                sx={{
                  position: 'absolute',
                  top: collapsed ? 12 : 27,
                  right: 12,
                  color: 'rgba(255, 255, 255, 0.75)',
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  width: 35,
                  height: 35,
                  zIndex: 1,
                  transition: (theme) => theme.transitions.create(['background-color', 'color'], {
                    duration: theme.transitions.duration.shortest
                  }),
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    color: 'rgba(255, 255, 255, 0.95)'
                  }
                }}
              >
                {collapsed
                  ? <LayoutSidebarLeftExpandIcon fontSize="small" />
                  : <LayoutSidebarLeftCollapseIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <img
              src={collapsed ? pwLogoCompact : poolwerxLogo}
              alt="Poolwerx"
              style={{
                height: collapsed ? '28px' : '56px',
                marginBottom: collapsed ? '12px' : '8px'
              }}
            />
            {!collapsed && (
              <Box sx={{ mb: 1, textAlign: 'center' }}>
                <Typography
                  variant="body1"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    lineHeight: 1.2,
                    fontSize: '1.1rem'
                  }}
                >
                  Inspector Tool
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.75)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    lineHeight: 1.2
                  }}
                >
                  Support Console
                </Typography>
              </Box>
            )}

            {/* User Menu */}
            <Box sx={{ pt: 1, borderTop: '1px solid rgba(255, 255, 255, 0.2)', width: '100%' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                </Box>
              ) : user ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: collapsed ? 'column' : 'row',
                      alignItems: 'center',
                      gap: collapsed ? 0.5 : 1,
                      width: '100%',
                    }}
                  >
                  <Tooltip
                    title={collapsed ? `${user.name}${version ? ` - v${version}` : ''}` : ''}
                    placement="right"
                    disableHoverListener={!collapsed}
                  >
                    <IconButton
                      onClick={handleUserMenuOpen}
                      sx={{
                        flexGrow: collapsed ? 0 : 1,
                        minWidth: 0,
                        borderRadius: 1,
                        py: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ width: '100%', justifyContent: collapsed ? 'center' : 'flex-start' }}
                      >
                        <Avatar
                          src={!imageError ? getProfileImageUrl(user) : undefined}
                          onError={() => setImageError(true)}
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: 'rgba(255, 255, 255, 0.2)'
                          }}
                        >
                          {getUserInitials(user)}
                        </Avatar>
                        {!collapsed && (
                          <>
                            <Box sx={{ flexGrow: 1, textAlign: 'left' }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.8rem',
                                  lineHeight: 1.2
                                }}
                              >
                                {user.name}
                              </Typography>
                              <Chip
                                label={getRoleDisplayName(user.role)}
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  bgcolor: getRoleHexColor(user.role),
                                  color: 'white',
                                  '& .MuiChip-label': {
                                    px: 0.5,
                                  },
                                }}
                              />
                            </Box>
                            <ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem' }} />
                          </>
                        )}
                      </Stack>
                    </IconButton>
                  </Tooltip>
                    <NotificationBell
                      placement="admin"
                      iconColor="white"
                      badgeVariant={collapsed ? 'dot' : 'standard'}
                      accountHeader={
                        collapsed ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
                            <Avatar
                              src={!imageError ? getProfileImageUrl(user) : undefined}
                              onError={() => setImageError(true)}
                              sx={{ width: 28, height: 28, bgcolor: '#ccc' }}
                            >
                              {getUserInitials(user)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {user.name}
                              </Typography>
                              <Chip
                                label={getRoleDisplayName(user.role)}
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  bgcolor: getRoleHexColor(user.role),
                                  color: 'white',
                                  '& .MuiChip-label': { px: 0.5 },
                                }}
                              />
                            </Box>
                          </Box>
                        ) : undefined
                      }
                    />
                  </Box>

                  <Menu
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    onClose={handleUserMenuClose}
                    className="no-print"
                    disablePortal={true}
                    disableAutoFocus={true}
                    disableEnforceFocus={true}
                    disableRestoreFocus={false}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                  >
                    <MenuItem disabled>
                      <Stack spacing={0.5} sx={{ minWidth: 200 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {user.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {user.email}
                        </Typography>
                        <Chip
                          label={getRoleDisplayName(user.role)}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            bgcolor: getRoleHexColor(user.role),
                            color: 'white',
                            alignSelf: 'flex-start',
                          }}
                        />
                      </Stack>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                      Sign Out
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ justifyContent: 'center', py: 1 }}>
                  <PersonIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '1.1rem' }} />
                  {!collapsed && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontWeight: 400,
                        fontSize: '0.8rem'
                      }}
                    >
                      Not signed in
                    </Typography>
                  )}
                </Stack>
              )}
            </Box>
          </Box>

          {/* Navigation */}
          <Box sx={{ flexGrow: 1 }}>
            <Navigation collapsed={collapsed} railWidth={drawerWidth} />
          </Box>

          {/* Version Display */}
          <Box
            sx={{
              p: collapsed ? 1 : 2,
              display: 'flex',
              justifyContent: 'center',
              borderTop: '1px solid rgba(255,255,255,0.1)'
            }}
          >
            <VersionDisplay variant="dark" compact={collapsed} />
          </Box>
        </Drawer>

        {/* Main Content column
          *
          * Inner flex column wraps `<main>` + `<DevEnvironmentBanner />` so
          * the sticky-bottom banner sticks within the main-content area
          * only and never spans the sidebar (the sidebar's own
          * `VersionDisplay` stays visible at all times).
          *
          * IMPORTANT: NO `overflow: *` on this column or its `<main>`
          * descendant. The outer wrapper uses `minHeight: 100vh` so a
          * non-visible overflow here would never trigger an inner
          * scrollbar (window scrolls instead) AND would still count this
          * Box as a "scrolling box" per CSS spec, trapping `position:
          * sticky` math for descendants - including BaseReportPage's
          * Results/Export/Fullscreen toolbar that needs to pin against
          * the window like inspector does.
          */}
        {/* `minWidth: 0` defeats flex's default `min-width: auto` so a wide
          * descendant (e.g. a 13-col platform-reports table) can't push the
          * page past 100vw. Doesn't create a scrolling box, so sticky still
          * works - the inner `<TableContainer>`'s own overflowX handles it.
          */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0, minHeight: '100vh' }}>
          <Box component="main" sx={{ flexGrow: 1, backgroundColor: '#f5f5f5' }}>
            <ReleaseNotesAutoLauncher />
            <Container
              maxWidth={isWide ? false : 'xl'}
              sx={{
                py: 4,
                px: 4,
                ...(isWide && { maxWidth: `${WIDE_CONTENT_MAX}px`, mx: 'auto' })
              }}
            >
              <Outlet />
            </Container>
          </Box>
          <DevEnvironmentBanner />
        </Box>
        </Box>
      </Box>

      <HelpFeedbackFab />
      <HelpFeedbackDrawer />
    </FeedbackUIProvider>
  )
}

export default AdminLayout